import { LocationContacts } from '@hakimo-ui/hakimo/feature-shared';
import { Alarm, DataVersion } from '@hakimo-ui/hakimo/types';
import { Page } from '@hakimo-ui/hakimo/ui-layout';
import { useCanVoiceCall, useLocalStorage } from '@hakimo-ui/hakimo/util';
import { useNavigate } from 'react-router-dom';
import AlarmMedia from './alarm-media/AlarmMedia';
import AlarmStatusPanel from './alarm-status/AlarmStatus';
import AlarmSummary from './alarm-summary/AlarmSummary';
import AlarmUpdates from './alarm-updates/AlarmUpdates';
import AlarmVideoTags from './alarm-video-tags/AlarmVideoTags';
import Floorplan from './floorplan/Floorplan';
import NeighboringEvents from './neighboring-events/NeighboringEvents';
import PageActions from './page-actions/PageActions';
import AlarmDetailsSOP from './sop/AlarmDetailsSOP';
import { useTrackAlarmEvents } from './useTrackAlarmEvents';

interface Props {
  alarm: Alarm;
  onRefresh: () => void;
  sharedToken?: string;
  dataVersion?: DataVersion;
}

function AlarmDetails(props: Props) {
  const { alarm, sharedToken, onRefresh, dataVersion } = props;
  const { onTrackResolve, onTrackTalkdown } = useTrackAlarmEvents(alarm.id);

  const navigate = useNavigate();
  const [alarmReturnPostResolve] = useLocalStorage<boolean>(
    'alarmReturnPostResolve'
  );
  const canVoiceCall = useCanVoiceCall();

  const onClickBack = () => {
    navigate('/alarms');
  };

  const onResolveAlarm = () => {
    onTrackResolve();

    if (alarmReturnPostResolve) {
      onClickBack();
    }
  };

  return (
    <Page
      title={alarm.id}
      subtitle="Alarm Details"
      pageActions={[
        <PageActions
          key="alarm-actions"
          // The product, not the media shape: AlarmDetailsRoot routes
          // `?product=scan` elsewhere, so this tree is always remote guarding,
          // including v3 RG alarms carrying scan-style frames. Drives the
          // action set and share resource type only.
          product="rg"
          alarmId={alarm.id}
          sharedToken={sharedToken}
          onRefresh={onRefresh}
          timestamp={alarm.timestamp}
          dataVersion={dataVersion}
        />,
      ]}
      onClickBack={!sharedToken ? onClickBack : undefined}
    >
      <div className="lg:grid-cols-3/4-1/4 bg-hakimo-light-bg dark:bg-hakimo-dark-bg grid grid-cols-1 items-start gap-2 pb-2">
        <div className="grid gap-2">
          <AlarmMedia
            alarm={alarm}
            sharedToken={sharedToken}
            onTalkdown={onTrackTalkdown}
            dataVersion={dataVersion}
          />
          <div className="grid grid-cols-2 gap-2">
            <AlarmStatusPanel
              alarm={alarm}
              onResolve={onResolveAlarm}
              dataVersion={dataVersion}
            />
            <AlarmVideoTags tags={alarm.tags || []} />
            <NeighboringEvents
              alarm={alarm}
              sharedToken={sharedToken}
              dataVersion={dataVersion}
            />
            <AlarmUpdates
              alarm={alarm}
              sharedToken={sharedToken}
              dataVersion={dataVersion}
            />
            <Floorplan alarm={alarm} />
            <AlarmDetailsSOP
              locationId={String(alarm.location?.id ?? '')}
              alarmTypeId={alarm.alarmTypeId ?? undefined}
              tenantId={alarm.tenant?.id ?? ''}
            />
          </div>
        </div>
        <div className="sticky z-0">
          <AlarmSummary alarm={alarm} />
          <div className="mt-2" />
          {canVoiceCall && alarm.location && (
            <div className="dark:bg-ondark-bg-1 bg-onlight-bg-1 min-h-[4rem] flex-1 overflow-auto rounded-lg px-6 pt-4">
              <LocationContacts locationId={alarm.location.id} />
            </div>
          )}
        </div>
      </div>
    </Page>
  );
}

export default AlarmDetails;
